import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/shares";
import type { NotificationEntry, NotificationListResponse } from "@/api/types";
import { useAuth } from "@/auth/stub";
import { Button } from "@/components/ui/button";
import { formatRelativeTime } from "@/lib/relativeTime";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Polling cadence. TanStack pauses it while the tab is hidden.
const POLL_MS = 60_000;

const notificationKeys = {
  list: (userId: string | null) => ["notifications", { userId }] as const,
};

export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const userId = useAuth((s) => s.user?.id ?? null);
  const key = notificationKeys.list(userId);

  const notifications = useQuery({
    queryKey: key,
    enabled: userId !== null,
    refetchInterval: POLL_MS,
    queryFn: async (): Promise<NotificationListResponse> => {
      const r = await fetchNotifications({ limit: 10 });
      if (r.kind === "error") throw new Error(r.message);
      return r.value;
    },
  });
  const items = notifications.data?.items ?? [];
  const unreadCount = notifications.data?.unread_count ?? 0;

  // Optimistic read-state updates so the dropdown doesn't wait for
  // the next poll; the settle-time invalidation reconciles with the
  // server either way.
  const patchCache = (
    update: (d: NotificationListResponse) => NotificationListResponse,
  ) => qc.setQueryData<NotificationListResponse>(key, (d) => d && update(d));

  const markOne = useMutation({
    mutationFn: async (id: string) => {
      const r = await markNotificationRead(id);
      if (r.kind === "error") throw new Error(r.message);
    },
    onMutate: (id) => {
      const now = new Date().toISOString();
      patchCache((d) => ({
        ...d,
        unread_count: Math.max(0, d.unread_count - 1),
        items: d.items.map((i) => (i.id === id ? { ...i, read_at: now } : i)),
      }));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const markAll = useMutation({
    mutationFn: async () => {
      const r = await markAllNotificationsRead();
      if (r.kind === "error") throw new Error(r.message);
    },
    onMutate: () => {
      const now = new Date().toISOString();
      patchCache((d) => ({
        ...d,
        unread_count: 0,
        items: d.items.map((i) => (i.read_at ? i : { ...i, read_at: now })),
      }));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const onClick = (entry: NotificationEntry) => {
    if (entry.read_at === null || entry.read_at === undefined) {
      markOne.mutate(entry.id);
    }
    if (entry.action_url) {
      window.location.assign(entry.action_url);
      return;
    }
    // share_received notifications don't have an action_url yet —
    // navigate to /shared-with-me so the recipient sees the share.
    if (entry.kind === "share_received") {
      void navigate({ to: "/shared-with-me" });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={
            unreadCount > 0
              ? t("notifications.open_with_count", "Notifications ({{n}} unread)", {
                  n: unreadCount,
                })
              : t("notifications.open", "Notifications")
          }
          className="relative"
        >
          <Bell className="h-4 w-4" aria-hidden />
          {unreadCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
              // The count is in the button's accessible name already.
              aria-hidden
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t("notifications.title", "Notifications")}</span>
          {unreadCount > 0 && (
            <button
              type="button"
              className="text-xs font-normal text-primary hover:underline"
              onClick={(e) => {
                // Don't close the dropdown; keep the user's flow intact.
                e.preventDefault();
                e.stopPropagation();
                markAll.mutate();
              }}
            >
              {t("notifications.mark_all_read", "Mark all as read")}
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && (
          <div className="px-2 py-4 text-center text-xs text-muted-foreground">
            {t("notifications.empty", "You're all caught up.")}
          </div>
        )}
        {items.map((n) => {
          const isRead = Boolean(n.read_at);
          return (
            <DropdownMenuItem
              key={n.id}
              className="flex-col items-start gap-0.5"
              onClick={() => onClick(n)}
              data-read={isRead}
            >
              <span
                className={cn(
                  "text-sm",
                  // Read items dim down so users can tell what
                  // they've already seen. Title loses bold; the
                  // unread dot disappears entirely.
                  isRead
                    ? "text-muted-foreground"
                    : "font-medium text-foreground",
                )}
              >
                {n.title}
                {!isRead && (
                  <span
                    className="ml-2 inline-block h-2 w-2 rounded-full bg-primary"
                    aria-label={t("notifications.unread", "unread")}
                  />
                )}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(n.created_at, i18n.language)}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
